import Modal from "@/components/shared/modal/modal";
import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	TextField,
	Theme,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useVendorsContext } from "../../context/VendorsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Vendor } from "../../models/Vendor";
import { createVendor } from "../../repositories/VendorsRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateVendor = ({ show, onHide }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const classes = useStyles();

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const {
		state: { vendors },
	} = useVendorsContext();

	const {
		register,
		control,
		setValue,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Vendor>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<Vendor> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await createVendor(data);
			if (res.ok) {
				setMessage("The vendor was created successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setOpenAlertMessage(true);
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (show) {
			reset();
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="createVendor"
					title="Create a new vendor"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "300px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={12} xs={12}>
											<Controller
												name="vendorName"
												rules={{ required: true }}
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.vendorName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.vendorName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>
										<Grid item md={12} xs={12}>
											<Controller
												name="nsblVendorId"
												rules={{ required: true, pattern: /^[0-9]*$/ }}
												control={control}
												render={({ field }) => (
													<TextField
														label="NSBL Vendor Id"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.nsblVendorId}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.nsblVendorId?.type === "required" && (
												<span className={classes.errorLabel}>
													This field is required
												</span>
											)}
											{errors.nsblVendorId?.type === "pattern" && (
												<span className={classes.errorLabel}>
													This field only accepts numbers
												</span>
											)}
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>

				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
